package fr.frux.feature.userprofile

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.frux.app.resources.Res
import fr.frux.app.resources.userprofile_harvests_organized_plural
import fr.frux.app.resources.userprofile_harvests_organized_singular
import fr.frux.app.resources.userprofile_member_since
import fr.frux.app.resources.userprofile_title_fallback
import fr.frux.app.resources.userprofile_trees_added_plural
import fr.frux.app.resources.userprofile_trees_added_singular
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.components.FruxCard
import fr.frux.core.designsystem.components.FruxTopBar
import fr.frux.core.designsystem.components.InitialsAvatar
import fr.frux.core.designsystem.components.LoadingState
import fr.frux.core.designsystem.components.SnackbarMessageEffect
import fr.frux.core.designsystem.components.message
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.shared.domain.model.UserProfile
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/**
 * Profil public d'un contributeur (#227) : pseudo, ancienneté, compteurs de contribution.
 * Lecture seule pour un visiteur anonyme ; un lecteur connecté peut signaler ou masquer ce compte
 * (ADR 0029).
 */
@Composable
fun UserProfileScreen(
    userId: Long,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: UserProfileViewModel = rememberUserProfileViewModel(userId),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    var showMenu by remember { mutableStateOf(false) }
    var showReportUserSheet by remember { mutableStateOf(false) }
    var showBlockDialog by remember { mutableStateOf(false) }

    SnackbarMessageEffect(
        trigger = state.moderationMessage,
        message = state.moderationMessage?.message(),
        snackbarHostState = snackbarHostState,
    ) { viewModel.consumeModerationMessage() }

    Scaffold(
        modifier = modifier,
        topBar = {
            FruxTopBar(
                title = state.profile?.username ?: stringResource(Res.string.userprofile_title_fallback),
                onBack = onBack,
                actions = {
                    val profile = state.profile
                    if (state.canModerate && profile != null) {
                        UserProfileActionsMenu(
                            username = profile.username,
                            isBlocked = profile.blockedByMe,
                            showMenu = showMenu,
                            onShowMenuChange = { showMenu = it },
                            onReportUser = { showReportUserSheet = true },
                            onBlock = { showBlockDialog = true },
                            onUnblock = viewModel::onUnblock,
                        )
                    }
                },
            )
        },
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = MaterialTheme.colorScheme.background,
    ) { paddingValues ->
        when {
            state.isLoading -> {
                LoadingState(Modifier.padding(paddingValues))
            }

            state.error != null -> {
                ErrorState(
                    message = state.error!!.userMessage(),
                    onRetry = viewModel::retry,
                    modifier = Modifier.padding(paddingValues),
                )
            }

            else -> {
                state.profile?.let { profile ->
                    ProfileContent(
                        profile = profile,
                        modifier =
                            Modifier
                                .fillMaxSize()
                                .padding(paddingValues)
                                .verticalScroll(rememberScrollState())
                                .padding(FruxSpacing.md),
                    )
                }
            }
        }
    }

    UserProfileModerationOverlays(
        username = state.profile?.username.orEmpty(),
        showReportUserSheet = showReportUserSheet,
        showBlockDialog = showBlockDialog,
        onReportUserSheetDismiss = { showReportUserSheet = false },
        onReportUser = { reason, details ->
            showReportUserSheet = false
            viewModel.onReportUser(reason, details)
        },
        onBlockDialogDismiss = { showBlockDialog = false },
        onBlockConfirmed = {
            showBlockDialog = false
            viewModel.onBlockConfirmed()
        },
    )
}

@Composable
private fun ProfileContent(
    profile: UserProfile,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Spacer(Modifier.height(FruxSpacing.lg))
        InitialsAvatar(username = profile.username, size = 72.dp)
        Spacer(Modifier.height(FruxSpacing.md))
        Text(
            text = profile.username,
            style = MaterialTheme.typography.headlineLarge,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(FruxSpacing.xs))
        Text(
            text = stringResource(Res.string.userprofile_member_since, formatMemberSince(profile.createdAt)),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Spacer(Modifier.height(FruxSpacing.lg))
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.md),
        ) {
            StatCard(
                count = profile.treesCount,
                label =
                    if (profile.treesCount == 1) {
                        stringResource(Res.string.userprofile_trees_added_singular)
                    } else {
                        stringResource(Res.string.userprofile_trees_added_plural)
                    },
                modifier = Modifier.weight(1f),
            )
            StatCard(
                count = profile.harvestsCount,
                label =
                    if (profile.harvestsCount == 1) {
                        stringResource(Res.string.userprofile_harvests_organized_singular)
                    } else {
                        stringResource(Res.string.userprofile_harvests_organized_plural)
                    },
                modifier = Modifier.weight(1f),
            )
        }
    }
}

@Composable
private fun StatCard(
    count: Int,
    label: String,
    modifier: Modifier = Modifier,
) {
    FruxCard(modifier = modifier) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(FruxSpacing.md),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = count.toString(),
                style = MaterialTheme.typography.headlineMedium,
                color = MaterialTheme.colorScheme.primary,
            )
            Spacer(Modifier.height(FruxSpacing.xs))
            Text(
                text = label,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
            )
        }
    }
}

/** L'app est monolingue français à ce jour (#176) : la petite table de mois est assumée. */
private val MONTHS_FR =
    listOf(
        "janvier",
        "février",
        "mars",
        "avril",
        "mai",
        "juin",
        "juillet",
        "août",
        "septembre",
        "octobre",
        "novembre",
        "décembre",
    )

@OptIn(ExperimentalTime::class)
internal fun formatMemberSince(epochMillis: Long): String {
    val date = Instant.fromEpochMilliseconds(epochMillis).toLocalDateTime(TimeZone.currentSystemDefault()).date
    return "${MONTHS_FR[date.monthNumber - 1]} ${date.year}"
}
